/**
 * Поля `/fields` (§9.3). На этапе 1 — список без конструктора: видно, какие
 * пользовательские поля есть, что они за тип и где применяются; менять можно
 * порядок и показ в таблице по умолчанию.
 *
 * Просмотр открыт всем (§9.3), поэтому список берётся из bootstrap — он
 * приезжает любому вошедшему. Правка требует права `fields.manage`: без него
 * переключатели остаются на месте, но не работают и объясняют причину (§11.4).
 * Полный список (вместе с архивными полями) отдаёт только `/api/fields`,
 * и запрашиваем мы его лишь тому, у кого право есть.
 */
import { useCallback, useEffect, useState } from 'react'
import type { FieldDefinition } from '@shared/types.ts'
import {
  Badge, Checkbox, EmptyState, IconArrowDown, IconArrowUp, IconButton, Skeleton,
} from '../../components/index.js'
import { api } from '../../lib/api.js'
import { useApp } from '../../store/app-store.js'
import { countOf, texts } from '../../texts.js'
import { AdminError, AdminPage, AdminReason } from '../admin/AdminPage.js'
import { adminForms, adminTexts } from '../admin/texts.admin.js'
import { errorText, hasPermission } from '../admin/admin-api.js'
import './FieldsScreen.css'

const t = adminTexts.fields

function byOrder(a: FieldDefinition, b: FieldDefinition): number {
  if (a.status !== b.status) return a.status === 'active' ? -1 : 1
  return a.sortOrder - b.sortOrder || a.id - b.id
}

export function FieldsScreen() {
  const toast = useApp((s) => s.toast)
  const permissions = useApp((s) => s.bootstrap?.user.permissions)
  const bootstrapFields = useApp((s) => s.bootstrap?.fields)
  const canManage = hasPermission(permissions, 'fields.manage')

  const [items, setItems] = useState<FieldDefinition[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoadError(null)
    // Без права на конструктор `/api/fields` ответит 403 — показываем bootstrap.
    if (!canManage) {
      setItems([...(bootstrapFields ?? [])].sort(byOrder))
      return
    }
    try {
      const res = await api.fields()
      setItems([...res.items].sort(byOrder))
    } catch (err) {
      setLoadError(errorText(err))
    }
  }, [canManage, bootstrapFields])

  useEffect(() => { void load() }, [load])

  async function toggleShow(field: FieldDefinition, showByDefault: boolean) {
    try {
      await api.updateField(field.id, { showByDefault })
      toast('success', t.toastSaved(field.name))
      await load()
    } catch (err) {
      toast('error', errorText(err))
    }
  }

  /** Порядок проставляем подряд (1…N): в базе он может быть нулевым у всех полей. */
  async function move(index: number, direction: -1 | 1) {
    if (!items) return
    const ordered = [...items]
    const current = ordered[index]
    const neighbour = ordered[index + direction]
    if (!current || !neighbour) return
    ordered[index] = neighbour
    ordered[index + direction] = current

    setBusy(true)
    try {
      for (const [position, field] of ordered.entries()) {
        const sortOrder = position + 1
        if (field.sortOrder === sortOrder) continue
        await api.updateField(field.id, { sortOrder })
      }
      toast('info', t.toastOrder)
      await load()
    } catch (err) {
      toast('error', errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const activeCount = (items ?? []).filter((f) => f.status === 'active').length

  return (
    <AdminPage
      wide
      title={texts.screen.fields.title}
      meta={items
        ? activeCount === items.length
          ? countOf(activeCount, adminForms.field)
          : `${countOf(activeCount, adminForms.field)} · в архиве ${items.length - activeCount}`
        : undefined}
      subtitle={texts.screen.fields.subtitle}
    >
      <p className="adm-note adm-note--muted">
        <span>{t.schemaNote}</span>
      </p>

      {!canManage ? <p className="adm-note">{t.viewOnly}</p> : null}

      {loadError ? <AdminError message={loadError} onRetry={() => { void load() }} /> : null}

      {!loadError && items === null ? (
        <div className="adm-table-wrap">
          <Skeleton rows={5} columns={[200, 160, 140, 160, 180]} label={adminTexts.common.loading} />
        </div>
      ) : null}

      {!loadError && items !== null && items.length === 0 ? (
        <EmptyState title={t.emptyTitle} text={t.emptyText} />
      ) : null}

      {!loadError && items !== null && items.length > 0 ? (
        <>
          <div className="adm-table-wrap">
            <table className="adm-table fields-table">
              <colgroup>
                <col className="fields-col--name" />
                <col className="fields-col--code" />
                <col className="fields-col--type" />
                <col className="fields-col--scope" />
                <col className="fields-col--required" />
                <col className="fields-col--show" />
                <col className="fields-col--order" />
                <col className="fields-col--status" />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="t-col">{t.colName}</th>
                  <th scope="col" className="t-col">{t.colCode}</th>
                  <th scope="col" className="t-col">{t.colType}</th>
                  <th scope="col" className="t-col">{t.colScope}</th>
                  <th scope="col" className="t-col">{t.colRequired}</th>
                  <th scope="col" className="t-col">{t.colShow}</th>
                  <th scope="col" className="t-col">{t.colOrder}</th>
                  <th scope="col" className="t-col">{t.colStatus}</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {items.map((field, index) => {
                  const archived = field.status === 'archived'
                  return (
                    <tr key={field.id} className={archived ? 'adm-row--archived' : undefined}>
                      <td>
                        <div className="adm-name">
                          <span className="adm-name__text">{field.name}</span>
                        </div>
                      </td>
                      <td className="adm-cell--code">{field.code}</td>
                      <td className="adm-cell--muted">{t.type[field.type]}</td>
                      <td className="adm-cell--muted">{t.scope[field.scope]}</td>
                      <td className="adm-cell--muted">{field.isRequired ? t.yes : t.no}</td>
                      <td>
                        <Checkbox
                          checked={field.showByDefault}
                          disabled={!canManage || archived}
                          aria-label={`${t.colShow}: ${field.name}`}
                          onChange={(checked) => { void toggleShow(field, checked) }}
                        />
                      </td>
                      <td>
                        <div className="adm-cell__row">
                          <IconButton
                            label={`${adminTexts.common.moveUp}: ${field.name}`}
                            icon={<IconArrowUp size={16} />}
                            disabled={!canManage || index === 0 || busy}
                            onClick={() => { void move(index, -1) }}
                          />
                          <IconButton
                            label={`${adminTexts.common.moveDown}: ${field.name}`}
                            icon={<IconArrowDown size={16} />}
                            disabled={!canManage || index === items.length - 1 || busy}
                            onClick={() => { void move(index, 1) }}
                          />
                        </div>
                      </td>
                      <td>
                        <Badge variant={archived ? 'neutral' : 'success'}>
                          {archived ? t.statusArchived : t.statusActive}
                        </Badge>
                      </td>
                      <td />
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {!canManage ? <AdminReason>{adminTexts.denied.fieldsManage}</AdminReason> : null}
        </>
      ) : null}
    </AdminPage>
  )
}
